import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Chip,
  Typography,
  CircularProgress,
  Paper,
  InputAdornment,
  List,
  ListItemText,
  ListItemButton,
  Alert,
} from "@mui/material";
import { Search, Print, Close } from "@mui/icons-material";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import { useLazyGetShipmentBySearchIdQuery } from "store/services/shipmentApi";
import {
  useLazyGetVendorProfilesByVendorIdQuery,
  useLazyGetVendorProfilesByVendorStaffIdQuery,
  useLazySearchVendorsQuery,
} from "store/services/vendorProfileApi";
import { useAuth } from "hooks";

const BulkInvoicePrintModal = ({ isOpen, onClose, entity, loggedInUser }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorResults, setVendorResults] = useState([]);
  const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);

  const { isVendor, isVendorStaff } = useAuth();
  const isRestrictedRole = isVendor || isVendorStaff;
  const typeRefId = loggedInUser?.typeRef;

  const [triggerSearch, { data, isLoading, isError, error }] =
    useLazyGetShipmentBySearchIdQuery();

  const [
    triggerGetVendorProfiles,
    { data: vendorProfilesData, isLoading: vendorProfilesLoading },
  ] = useLazyGetVendorProfilesByVendorIdQuery();

  const [
    triggerGetVendorStaffProfiles,
    { data: vendorStaffProfilesData, isLoading: vendorStaffProfilesLoading },
  ] = useLazyGetVendorProfilesByVendorStaffIdQuery();

  const [triggerVendorSearch, { data: vendorData, isLoading: vendorLoading }] =
    useLazySearchVendorsQuery();

  // Unified profile data for restricted roles
  const restrictedProfiles = isVendor
    ? vendorProfilesData?.data
    : isVendorStaff
    ? vendorStaffProfilesData?.data
    : null;

  const restrictedProfilesLoading = isVendor
    ? vendorProfilesLoading
    : vendorStaffProfilesLoading;

  // Fetch profiles on mount for restricted roles
  useEffect(() => {
    if (!typeRefId) return;
    if (isVendor) triggerGetVendorProfiles(typeRefId);
    else if (isVendorStaff) triggerGetVendorStaffProfiles(typeRefId);
  }, [isVendor, isVendorStaff, typeRefId, triggerGetVendorProfiles, triggerGetVendorStaffProfiles]);

  // Auto-select first profile once loaded
  useEffect(() => {
    if (isRestrictedRole && restrictedProfiles?.length) {
      setSelectedVendor(restrictedProfiles[0]);
    }
  }, [isRestrictedRole, restrictedProfiles]);

  // Shipment search — debounced on searchTerm
  useEffect(() => {
    if (searchTerm.trim().length >= 3 && selectedVendor?._id) {
      const delayDebounceFn = setTimeout(() => {
        triggerSearch({ shipmentId: searchTerm, vendorId: selectedVendor._id });
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, triggerSearch, selectedVendor]);

  // Admin vendor search
  useEffect(() => {
    if (vendorSearchTerm.trim().length >= 2) {
      const delay = setTimeout(() => {
        const currentEntity = loggedInUser.entities.find(
          (e) => e.entity._id === entity._id
        );
        triggerVendorSearch({
          account: currentEntity.entity.account._id,
          entity: entity._id,
          query: vendorSearchTerm,
        });
      }, 500);
      return () => clearTimeout(delay);
    } else {
      setVendorResults([]);
      setShowVendorSuggestions(false);
    }
  }, [vendorSearchTerm, triggerVendorSearch, loggedInUser, entity]);

  useEffect(() => {
    if (vendorData?.data) {
      setVendorResults(vendorData.data);
      setShowVendorSuggestions(true);
    } else {
      setVendorResults([]);
      setShowVendorSuggestions(false);
    }
  }, [vendorData]);

  useEffect(() => {
    if (data) {
      setSearchResults(data);
      setShowSuggestions(true);
    }
  }, [data]);

  const handleSelectShipment = (shipment) => {
    const isAlreadySelected = selectedShipments.some((s) => s._id === shipment._id);
    if (!isAlreadySelected) {
      setSelectedShipments([...selectedShipments, shipment]);
    }
    setSearchTerm("");
    setSearchResults([]);
    setShowSuggestions(false);
  };

  const handleRemoveShipment = (shipmentId) => {
    setSelectedShipments(selectedShipments.filter((s) => s._id !== shipmentId));
  };

  const handlePrintAll = () => {
    if (selectedShipments.length === 0) return;
    try {
      const printWindow = window.open("", "", "width=900,height=700");
      if (!printWindow) {
        console.error("Failed to open print window");
        return;
      }
      let allInvoicesHTML = `
      <html>
        <head>
          <title>Bulk Shipment Invoices</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 14px; margin: 0; padding: 0; }
            .invoice-page { width: 100%; min-height: 100vh; padding: 30px; box-sizing: border-box; page-break-after: always; position: relative; }
            .invoice-page:last-child { page-break-after: avoid; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px; }
            th, td { padding: 4px 6px; border-bottom: 1px solid #000; }
            th { border-top: 1px solid #000; text-align: left; }
            .right { text-align: right; }
            .center { text-align: center; }
            .total-row { border-top: 2px solid #000 !important; }
            hr { border: 0; border-top: 1px dashed #000; margin: 10px 0; }
            .flex { display: flex; justify-content: space-between; align-items: flex-start; }
            @media print {
              @page { size: A4 landscape; margin: 0.8cm; }
              body { margin: 0; padding: 0; }
              .invoice-page { padding: 20px; page-break-after: always; break-after: page; }
              .invoice-page:last-child { page-break-after: avoid; break-after: avoid; }
            }
          </style>
        </head>
        <body>
      `;
      selectedShipments.forEach((shipment) => {
        const pricesRes = { data: shipment.prices || [] };
        const invoiceHTML = generateInvoiceHTML(shipment, pricesRes, entity, loggedInUser);
        allInvoicesHTML += `<div class="invoice-page">${invoiceHTML}</div>`;
      });
      allInvoicesHTML += `</body></html>`;
      printWindow.document.open();
      printWindow.document.write(allInvoicesHTML);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 800);
    } catch (error) {
      console.error("Error printing bulk invoices:", error);
    }
  };

  const generateInvoiceHTML = (shipment, pricesRes, entity, loggedInUser) => {
    const pickupTime = shipment?.shipmentPickupTime
      ? new Date(shipment.shipmentPickupTime)
      : new Date();
    const formatDate = (date) => {
      const adDateString = date.toLocaleDateString("en-CA").replace(/-/g, "/");
      const { ADToBS } = require("bikram-sambat-js");
      const bsDate = ADToBS(adDateString);
      return {
        miti: bsDate,
        time: date.toLocaleTimeString("en-GB", { hour12: false }),
        date: date.toLocaleDateString("en-GB").split("/").reverse().join("/"),
      };
    };
    const { miti, time, date } = formatDate(pickupTime);
    const showVatNumber =
      shipment?.entity?.additionalInfo?.vatNumber?.display !== false;
    const sender = shipment?.customerId
      ? { name: shipment?.customerId?.customerUser?.name || "--" }
      : { name: shipment?.vendor?.shopName || "--" };
    const VAT_RATE = 0.13;
    const roundMoney = (value) => Number(Number(value || 0).toFixed(2));
    const getDescription = (item) => {
      const name = item?.feeItem?.name || "Shipment Charge";
      if (name === "BASE - Standard charge") return "Service Charge";
      if (name.toLowerCase().includes("freight")) return "Freight Charge";
      if (name.toLowerCase().includes("documentation")) return "Documentation & Processing Fee";
      return name;
    };
    const particulars = pricesRes?.data?.length
      ? pricesRes.data
          .filter((item) => item.chargeType !== "Cash on Delivery")
          .map((item) => ({
            description: getDescription(item),
            amount: roundMoney(item.charge),
            rate: roundMoney(Number(item.charge || 0) / (1 + VAT_RATE)),
            vat: roundMoney(Number(item.charge || 0) - roundMoney(Number(item.charge || 0) / (1 + VAT_RATE))),
          }))
      : [{ description: "Shipment Charge", rate: 0.0, amount: 0.0 }];
    const formatNumWithCommas = (num) =>
      num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0.00";
    const getWeightLabel = (s) => {
      if (s?.weight) return `${s.weight} Kg`;
      if (s?.unit?.value && s?.unit?.unitType?.name) {
        return s.unit.unitType.name.toLowerCase() === "weight"
          ? `${s.unit.value} Kg`
          : `${s.unit.value} ${s.unit.unitType.name}`;
      }
      return "N/A";
    };
    const getWeightValue = (s) => {
      if (s?.weight) return s.weight;
      if (s?.unit?.value) return s.unit.value;
      return 1;
    };
    const numberToWords = (num) => {
      const integerPart = Math.floor(num);
      const decimalPart = Math.round((num - integerPart) * 100);
      const units = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
      const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
      const thousands = ["", "Thousand", "Million"];
      const convertLessThanThousand = (n) => {
        if (n === 0) return "";
        if (n < 20) return units[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + units[n % 10] : "");
        return units[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convertLessThanThousand(n % 100) : "");
      };
      let word = "";
      let thousandIndex = 0;
      let tempNum = integerPart;
      if (tempNum === 0) {
        word = "Zero";
      } else {
        while (tempNum > 0) {
          if (tempNum % 1000 !== 0) {
            word = convertLessThanThousand(tempNum % 1000) +
              (thousands[thousandIndex] ? " " + thousands[thousandIndex] : "") +
              (word ? " " + word : "");
          }
          tempNum = Math.floor(tempNum / 1000);
          thousandIndex++;
        }
      }
      if (decimalPart > 0) word += " and " + convertLessThanThousand(decimalPart) + " Cents";
      return word || "Zero";
    };
    return `
      <div class="flex" style="display: flex; align-items: center;">
        <div style="display: flex; align-items: flex-start; width: 100%;">
          <div style="flex: 1 0 auto; justify-content: center; align-items: center;">
            <div style="display: flex; align-items: center; margin-bottom: 8px;">
              <img src="${entity?.logo?.url || ""}" alt="Logo" style="height: 80px;" />
              <div style="display: flex; flex-direction: column; margin-left: 10px;">
                <div style="font-size: 16px; font-weight: bold; color: #e53935;">${entity?.name || ""}</div>
                <div style="font-size: 14px;">${entity?.location || ""}</div>
              </div>
            </div>
          </div>
          <div style="text-align: right; font-size: 12px; margin-top: 10px; flex: 1;">
            <div>Tel No.: ${entity?.telNo || ""}</div>
            ${showVatNumber ? `<div>VAT No.: ${entity?.additionalInfo?.vatNumber?.value || ""}</div>` : ""}
          </div>
        </div>
      </div>
      <div style="text-align: center; margin-top: 10px;">
        <h2 style="display: inline-block; font-size: 16px;">Sales Receipt</h2>
      </div>
      <hr />
      <table style="width: 100%;">
        <tr>
          <td style="border: none;"><strong>Receipt No:</strong> ${shipment?.shipmentId || ""}</td>
          <td style="border: none;"><strong>Mode:</strong> To Pay</td>
          <td style="border: none;"><strong>Miti:</strong> ${miti}</td>
          <td style="border: none;"><strong>Time:</strong> ${time}</td>
        </tr>
        <tr>
          <td style="border: none;"><strong>Customer:</strong> ${sender?.name || "--"}</td>
          ${showVatNumber ? `<td style="border: none;"><strong>VAT No:</strong> </td>` : `<td style="border: none;"></td>`}
          <td style="border: none;"><strong>Date:</strong> ${date}</td>
          <td style="border: none;"></td>
        </tr>
      </table>
      <hr />
      <table style="width: 100%; border-collapse: collapse;">
        <tr style="border: none;">
          <td style="border: none;"><strong>Sender:</strong> ${sender.name}</td>
          ${showVatNumber ? `<td style="border: none;"><strong>VAT No:</strong> </td>` : `<td style="border: none;"></td>`}
          <td style="border: none;"><strong>From:</strong> ${shipment?.sourceBranch?.name || "--"}</td>
          <td style="border: none;"></td>
        </tr>
        <tr style="border: none;">
          <td style="border: none;"><strong>Receiver:</strong> ${shipment?.receiver?.customerUser?.name || "Nepal Can Transport"}</td>
          <td style="border: none;"></td>
          <td style="border: none;"><strong>To:</strong> ${shipment?.destinationBranch?.name || "--"}</td>
          <td style="border: none;"></td>
        </tr>
        <tr style="border: none;">
          <td style="border: none;"><strong>Phone:</strong> ${shipment?.receiver?.phone || ""}</td>
          <td style="border: none;"><strong>Address:</strong> ${shipment?.receiver?.address || "--"}</td>
          <td style="border: none;"><strong>G.Value:</strong></td>
        </tr>
      </table>
      <table>
        <thead>
          <tr>
            <th>SN.</th>
            <th>Particular</th>
            <th>${shipment?.weight ? "Weight" : shipment?.unit?.unitType?.name?.toLowerCase() === "weight" ? "Weight" : shipment?.unit?.value ? "Unit" : "Weight"}</th>
            <th>Rate</th>
            <th>Taxable Amt</th>
            <th>VAT 13%</th>
            <th class="right">Total Amt</th>
          </tr>
        </thead>
        <tbody>
          ${particulars.map((item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${item?.description || "Shipment Charge"}</td>
              <td>${getWeightLabel(shipment)}</td>
              <td>Rs. ${formatNumWithCommas((item?.rate / getWeightValue(shipment)).toFixed(2) || "0.00")}</td>
              <td>Rs. ${formatNumWithCommas(item?.rate?.toFixed(2)) || "0.00"}</td>
              <td>Rs. ${formatNumWithCommas(item?.vat?.toFixed(2)) || "0.00"}</td>
              <td class="right">Rs. ${formatNumWithCommas(item?.amount?.toFixed(2)) || "0.00"}</td>
            </tr>`).join("")}
          <tr class="total-row">
            <td colspan="6" style="border: none;"><strong>Total</strong></td>
            <td class="right"><strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item?.amount, 0)?.toFixed(2)) || "0.00"}</strong></td>
          </tr>
        </tbody>
      </table>
      <div style="margin: 8px 0;">
        <hr />
        <p class="right">Total Taxable Amount: <strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item.rate, 0).toFixed(2))}</strong></p>
        <p class="right">VAT 13%: <strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item.vat, 0).toFixed(2))}</strong></p>
        <p class="right">Grand Total: <strong>Rs. ${formatNumWithCommas(particulars.reduce((sum, item) => sum + item.amount, 0).toFixed(2))}</strong></p>
        <p style="font-size: 11px;">Formula: Base Amount = Original Amount ÷ 1.13; Base Amount + VAT = Original Amount.</p>
      </div>
      <div style="margin-bottom: 8px;">
        <p><strong>In words:</strong> ${numberToWords(Math.round(particulars.reduce((sum, item) => sum + item?.amount, 0)))} Rupees Only.</p>
        <hr />
        <p><strong>Remarks:</strong></p>
        <p>${shipment?.remarks || ""}</p>
      </div>
      <div class="flex footer">
        <div></div>
        <div class="right">
          <p>
            <strong>Print Time:</strong> ${new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "medium" })}
            <br />
            <div style="margin-top: 2px;"><strong>Booking Officer:</strong> ${loggedInUser?.name || "N/A"}</div>
          </p>
        </div>
      </div>
      <div style="text-align: center; margin-top: 20px; font-size: 11px;">
        <p>Thank you for choosing ${entity?.name || " "} as your logistics partner!</p>
      </div>
    `;
  };

  const getSenderName = (shipment) => {
    if (shipment?.customerId?.customerUser?.name) return shipment.customerId.customerUser.name;
    if (shipment?.vendor?.shopName) return shipment.vendor.shopName;
    return "N/A";
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} width="800px">
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Bulk Invoice Print
        </Typography>

        {/* Vendor Section */}
        {isRestrictedRole ? (
          <Box sx={{ mb: 3 }}>
            {restrictedProfilesLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Your Vendor Profile
                </Typography>
                <List dense>
                  {restrictedProfiles?.map((profile) => (
                    <ListItemButton
                      key={profile._id}
                      selected={selectedVendor?._id === profile._id}
                      onClick={() => {
                        setSelectedVendor(profile);
                        setSearchTerm("");
                        setSelectedShipments([]);
                      }}
                    >
                      <ListItemText
                        primary={profile.shopName}
                        secondary={`${profile.email || ""} | ${profile.phone || ""}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ position: "relative", mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search Vendor by shop name"
              value={selectedVendor ? selectedVendor.shopName : vendorSearchTerm}
              onChange={(e) => {
                if (selectedVendor) setSelectedVendor(null);
                setVendorSearchTerm(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {vendorLoading && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    )}
                    {selectedVendor && (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedVendor(null);
                            setVendorSearchTerm("");
                          }}
                        >
                          Clear
                        </Button>
                      </InputAdornment>
                    )}
                  </>
                ),
              }}
            />
            {showVendorSuggestions && vendorResults.length > 0 && (
              <Paper
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                  mt: 1,
                  boxShadow: 3,
                }}
              >
                <List>
                  {vendorResults.map((vendor) => (
                    <ListItemButton
                      key={vendor._id}
                      onClick={() => {
                        setSelectedVendor(vendor);
                        setVendorSearchTerm(vendor.shopName);
                        setShowVendorSuggestions(false);
                        setSearchTerm("");
                        setSelectedShipments([]);
                      }}
                    >
                      <ListItemText
                        primary={vendor.shopName}
                        secondary={`${vendor.email || ""} | ${vendor.phone || ""}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {/* Selected Vendor Info */}
        {selectedVendor && (
          <Alert
            severity="success"
            sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Box>
              Selected Vendor: <strong>{selectedVendor.shopName}</strong> ({selectedVendor.email})
            </Box>
            {/* Admin only — restricted roles can't switch */}
            {!isRestrictedRole && (
              <Button color="inherit" size="small" onClick={() => setSelectedVendor(null)}>
                Change
              </Button>
            )}
          </Alert>
        )}

        {/* Shipment Search */}
        <Box sx={{ position: "relative", mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by Shipment ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: isLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />
          {showSuggestions && searchResults.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                maxHeight: "300px",
                overflowY: "auto",
                mt: 1,
                boxShadow: 3,
              }}
            >
              <List>
                {searchResults.map((shipment) => (
                  <ListItemButton
                    key={shipment._id}
                    onClick={() => handleSelectShipment(shipment)}
                    disabled={selectedShipments.some((s) => s._id === shipment._id)}
                  >
                    <ListItemText
                      primary={shipment.shipmentId}
                      secondary={`${getSenderName(shipment)} → ${shipment?.receiver?.customerUser?.name || "N/A"} | ${shipment?.sourceBranch?.name || "N/A"} → ${shipment?.destinationBranch?.name || "N/A"}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
          {showSuggestions && searchResults.length === 0 && !isLoading && searchTerm.length >= 3 && (
            <Paper
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                mt: 1,
                p: 2,
                boxShadow: 3,
              }}
            >
              <Typography color="text.secondary">No shipments found</Typography>
            </Paper>
          )}
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.data?.message || "Failed to search shipments"}
          </Alert>
        )}

        {/* Selected Shipments */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Selected Shipments ({selectedShipments.length})
          </Typography>
          {selectedShipments.length === 0 ? (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "background.paper",
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography color="text.secondary">
                No shipments selected. Search and select shipments to print receipts.
              </Typography>
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 2,
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedShipments.map((shipment) => (
                  <Chip
                    key={shipment._id}
                    label={
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {shipment.shipmentId}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                          {getSenderName(shipment)} →{" "}
                          {shipment?.receiver?.customerUser?.name || "N/A"}
                        </Typography>
                      </Box>
                    }
                    onDelete={() => handleRemoveShipment(shipment._id)}
                    deleteIcon={<Close />}
                    sx={{
                      height: "auto",
                      py: 1,
                      px: 1.5,
                      "& .MuiChip-label": { display: "block", whiteSpace: "normal" },
                    }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrintAll}
            disabled={selectedShipments.length === 0}
          >
            Print All ({selectedShipments.length})
          </Button>
        </Box>

        {selectedShipments.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Clicking "Print All" will open {selectedShipments.length} print dialog(s) sequentially. Please wait for each dialog to complete.
          </Alert>
        )}
      </Box>
    </BaseModal>
  );
};

export default BulkInvoicePrintModal;
